import { css, SerializedStyles } from '@emotion/react';
import {
  ActionType,
  ActorTypeSystem,
  ActorTypeWeave,
  AuditActor,
  AuditEvent,
  SearchAuditEventsResponse,
} from '@frontend/api-phone-audits';
import { i18next, useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import {
  Avatar,
  Button,
  Chip,
  InfoIcon,
  PopoverDialog,
  Table,
  TableColumnConfig,
  Text,
  TextLink,
  Truncated,
  usePopoverDialog,
} from '@frontend/design-system';
import {
  ACTION_CHIP_CONFIG,
  OBJECT_SETTINGS_PATH,
  SETTING_TYPE_LABELS,
  UNKNOWN_LABEL,
} from './phone-system-history-config';
import { AuditEventFilters, PhoneSystemHistoryFilters } from './phone-system-history-filters';
import { formatEventTime, getEventSummaryString } from './utils/audit-formatters';

type PhoneSystemHistoryTableProps = {
  data: SearchAuditEventsResponse | undefined;
  isLoading: boolean;
  canEditUsers: boolean;
  // Filters
  filters: AuditEventFilters;
  onFiltersChange: (filters: AuditEventFilters) => void;
  onDetailsClick: (event: AuditEvent) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  isTrayOpen?: boolean;
  selectedEventId?: string;
};

const selectedRowStyles = css`
  background-color: var(--fabric-table-row-selected);
`;

export function PhoneSystemHistoryTable({
  data,
  isLoading,
  canEditUsers,
  filters,
  onFiltersChange,
  searchValue,
  setSearchValue,
  onDetailsClick,
  isTrayOpen,
  selectedEventId,
}: PhoneSystemHistoryTableProps) {
  const { t } = useTranslation('phone');

  const events = data?.auditEvents ?? [];

  return (
    <div
      css={css`
        background-color: #ffffff;
        border-radius: 8px;
        padding: 24px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      `}
    >
      <Table
        tableInstanceId='phone-system-history-table'
        colConfig={getColumnConfig(t, canEditUsers, setSearchValue, onDetailsClick)}
        data={events}
        isPaginated
        clientPaginationConfig={{
          defaultRowsPerPage: 50,
          rowsPerPageOptions: [25, 50, 100],
        }}
        isLoading={isLoading}
        styleConfig={{
          rows: (rowData: AuditEvent): SerializedStyles | undefined =>
            selectedEventId && rowData.id === selectedEventId ? selectedRowStyles : undefined,
        }}
        emptyStateConfig={{
          header: t('No History Available'),
          description: t('No phone system changes have been recorded yet.'),
        }}
        hasResponsiveColWidths
        customToolbarRender={() => (
          <PhoneSystemHistoryFilters
            filters={filters}
            onFiltersChange={onFiltersChange}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            isCompact={isTrayOpen}
          />
        )}
        wrapperStyle={css`
          .table-toolbar {
            display: flex;
            flex-direction: column;
            gap: var(--fabric-space-gap-sm);
          }
        `}
      />
    </div>
  );
}

function getColumnConfig(
  t: (key: string) => string,
  canEditUsers: boolean,
  setSearchValue: (value: string) => void,
  onDetailsClick: (event: AuditEvent) => void
): TableColumnConfig<AuditEvent>[] {
  return [
    {
      Header: t('Current Name'),
      id: 'currentName',
      accessor: (event: AuditEvent) => event,
      cellRenderer: (event: AuditEvent) => <CurrentNameCell event={event} />,
      width: 180,
      disableSortBy: true,
    },
    {
      Header: t('Setting Type'),
      id: 'settingType',
      accessor: (event: AuditEvent) => SETTING_TYPE_LABELS[event.objectType ?? '']?.label ?? UNKNOWN_LABEL,
      width: 140,
      disableSortBy: true,
    },
    {
      Header: t('Action'),
      id: 'action',
      accessor: (event: AuditEvent) => event.action,
      cellRenderer: (action: ActionType) => {
        const config = ACTION_CHIP_CONFIG[action ?? ''] ?? { label: UNKNOWN_LABEL, variant: 'warn' as const };
        return <Chip variant={config.variant}>{config.label}</Chip>;
      },
      width: 100,
      disableSortBy: true,
    },
    {
      Header: t('Details'),
      id: 'details',
      accessor: (event: AuditEvent) => event,
      cellRenderer: (event: AuditEvent) => {
        const detail = getDetailsText(event);
        return (
          // Wrap the detail text in a div to ensure that the Truncated component has a parent with a defined width,
          // allowing it to properly truncate the text when necessary.
          <div
            css={css`
              max-width: 100%;
            `}
          >
            <Truncated as={TextLink} textAlign='left' onClick={() => onDetailsClick(event)}>
              {detail}
            </Truncated>
          </div>
        );
      },
      width: 140,
      disableSortBy: true,
    },
    {
      Header: t('Date'),
      id: 'date',
      accessor: (event: AuditEvent) => event.createTime,
      cellRenderer: (createTime: string) => {
        const formatted = formatEventTime(createTime);
        return (
          <Truncated as={Text} textAlign='left'>
            {formatted}
          </Truncated>
        );
      },
      width: 220,
      disableSortBy: true,
    },
    {
      Header: t('Updated By'),
      id: 'updatedBy',
      accessor: (event: AuditEvent) => event.actor,
      cellRenderer: (actor: AuditActor) => <UpdatedByCell actor={actor} canEditUsers={canEditUsers} />,
      width: 200,
      disableSortBy: true,
    },
    {
      Header: <ObjectIdHeader />,
      id: 'objectId',
      accessor: (event: AuditEvent) => event.id ?? '',
      cellRenderer: (id: string) => (
        <Truncated as={Text} textAlign='left'>
          {id}
        </Truncated>
      ),
      width: 240,
      disableSortBy: true,
    },
    {
      Header: '',
      id: 'actions',
      accessor: (event: AuditEvent) => event.id ?? '',
      sticky: 'right' as const,
      cellRenderer: (id: string) => <Button iconName='search' variant='secondary' onClick={() => setSearchValue(id)} />,
      width: 50,
      disableSortBy: true,
    },
  ];
}

function CurrentNameCell({ event }: { event: AuditEvent }) {
  const { navigate } = useSettingsNavigate();
  const settingsPath = OBJECT_SETTINGS_PATH[event.objectType ?? ''];

  if (!settingsPath) {
    return <Text>{event.objectName}</Text>;
  }

  return <TextLink onClick={() => navigate({ to: settingsPath })}>{event.objectName}</TextLink>;
}

function ObjectIdHeader() {
  const { t } = useTranslation('phone');
  const { getDialogProps, getTriggerProps } = usePopoverDialog({ placement: 'bottom' });

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: var(--fabric-space-gap-xxs);
      `}
    >
      <span>{t('Object ID')}</span>

      <Button
        {...getTriggerProps()}
        variant='tertiary'
        size='small'
        css={css`
          padding: var(--fabric-space-padding-xxs);
          min-width: unset;
          color: var(--colors-neutral-50);
        `}
      >
        <InfoIcon size={14} />
      </Button>

      <PopoverDialog
        {...getDialogProps()}
        css={css`
          padding: var(--fabric-space-padding-md);
          max-width: 400px;
          display: flex;
          flex-direction: column;
          gap: var(--fabric-space-gap-xs);
        `}
      >
        <Text weight='bold'>{t('Object ID')}</Text>
        <Text size='medium'>
          {t(
            'This is the unique identifier of any Phone System object in your Phone System. For example, this could be a specific Call Group or Call Route.'
          )}
        </Text>
        <Text size='medium'>
          {t('This identifier stays consistent even if a Phone System object is deleted or renamed.')}
        </Text>
      </PopoverDialog>
    </div>
  );
}

function UpdatedByCell({ actor, canEditUsers }: { actor: AuditActor; canEditUsers: boolean }) {
  const { t } = useTranslation('phone');
  const { navigate } = useSettingsNavigate();
  const isSystem = actor?.actorType === ActorTypeSystem;
  const isWeaveEmployee = actor?.actorType === ActorTypeWeave;

  if (isSystem) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: var(--fabric-space-gap-xs);
        `}
      >
        <Avatar iconName='weave-logo' size='xs' disableHoverLabel />
        <Text size='medium'>{actor?.userName ?? 'Weave System'}</Text>
      </div>
    );
  }

  if (isWeaveEmployee) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: var(--fabric-space-gap-xs);
        `}
      >
        <Avatar iconName='weave-logo' size='xs' disableHoverLabel />
        <Text size='medium'>
          {actor?.userName}{' '}
          <span
            css={css`
              color: var(--colors-neutral-50);
            `}
          >
            {t('(Weave User)')}
          </span>
        </Text>
      </div>
    );
  }

  const canLinkToUser = canEditUsers && !!actor?.userId;

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: var(--fabric-space-gap-xs);
      `}
    >
      <Avatar name={actor?.userName ?? ''} size='xs' disableHoverLabel />
      {canLinkToUser ? (
        <TextLink
          css={css`
            text-align: left;
          `}
          onClick={() => navigate({ to: '/organization/users/:id', params: { id: actor?.userId ?? '' } })}
        >
          {actor?.userName}
        </TextLink>
      ) : (
        <Text size='medium'>{actor?.userName}</Text>
      )}
    </div>
  );
}

// Returns a string that summarizes the changes in an audit event. If there are no changes, it returns '--'. If there
// is one change, it returns the summary of that change. If there are multiple changes, it returns a string indicating
// the number of changes.
function getDetailsText(event: AuditEvent): string {
  const { changes } = event;
  if (!changes || changes.length === 0) return '--';

  if (changes.length === 1) {
    const summary = getEventSummaryString(event);
    return summary || '--';
  }

  return i18next.t('{{count}} changes', { count: changes.length, ns: 'phone' });
}
